import {
  FLOW_LABELS,
  FLOW_WEIGHT,
  formatShort,
  painLabel,
  toISODate,
  addDays,
  type CyclePrediction,
  type DayLog,
  type FlowLevel,
} from './cycle';

export interface CycleStats {
  /** Number of logged days in the window. */
  loggedDays: number;
  avgPain: number;
  maxPain: number;
  /** Days with pain >= 7. */
  flareDays: number;
  /** Days with pain >= 4. */
  difficultDays: number;
  avgHeartRate: number | null;
  avgHrv: number | null;
  minHrv: number | null;
  /** Symptom frequency, most frequent first. */
  symptomFreq: Array<{ name: string; count: number }>;
  flowCounts: Array<{ flow: FlowLevel; label: string; days: number }>;
  /** Daily series for charting (every day in window; null pain when unlogged). */
  daily: Array<{ date: string; label: string; pain: number | null; flowWeight: number | null }>;
}

/** Aggregate stats over the trailing `days` window ending today. */
export function computeStats(logs: DayLog[], days = 30, today: Date = new Date()): CycleStats {
  const byDate = new Map(logs.map((l) => [l.date, l]));

  const windowLogs: DayLog[] = [];
  const daily: CycleStats['daily'] = [];

  for (let offset = -(days - 1); offset <= 0; offset++) {
    const date = addDays(today, offset);
    const iso = toISODate(date);
    const log = byDate.get(iso);
    if (log) windowLogs.push(log);
    daily.push({
      date: iso,
      label: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      pain: log?.pain ?? null,
      flowWeight: log ? FLOW_WEIGHT[log.flow] : null,
    });
  }

  const pains = windowLogs.map((l) => l.pain);
  const hrs = windowLogs.map((l) => l.heartRate).filter((v): v is number => typeof v === 'number');
  const hrvs = windowLogs.map((l) => l.hrv).filter((v): v is number => typeof v === 'number');

  const symptomCounts = new Map<string, number>();
  for (const log of windowLogs) {
    for (const s of log.symptoms) {
      symptomCounts.set(s, (symptomCounts.get(s) ?? 0) + 1);
    }
  }

  const flowCounts = (Object.keys(FLOW_LABELS) as FlowLevel[])
    .filter((f) => f !== 'none')
    .map((flow) => ({
      flow,
      label: FLOW_LABELS[flow],
      days: windowLogs.filter((l) => l.flow === flow).length,
    }));

  const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

  return {
    loggedDays: windowLogs.length,
    avgPain: avg(pains) ?? 0,
    maxPain: pains.length ? Math.max(...pains) : 0,
    flareDays: windowLogs.filter((l) => l.pain >= 7).length,
    difficultDays: windowLogs.filter((l) => l.pain >= 4).length,
    avgHeartRate: avg(hrs) ? Math.round(avg(hrs)!) : null,
    avgHrv: avg(hrvs) ? Math.round(avg(hrvs)!) : null,
    minHrv: hrvs.length ? Math.min(...hrvs) : null,
    symptomFreq: [...symptomCounts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count),
    flowCounts,
    daily,
  };
}

/* ------------------------------------------------------------------ */
/* Doctor summary download                                             */
/* ------------------------------------------------------------------ */

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

/** Build a self-contained, printable HTML progress report. */
export function buildDoctorReportHtml(
  logs: DayLog[],
  stats: CycleStats,
  prediction: CyclePrediction | null,
  generatedAt: Date = new Date(),
): string {
  const rows = [...logs]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 30)
    .map(
      (log) => `<tr>
        <td>${formatShort(log.date)}</td>
        <td>${log.pain}/10 (${painLabel(log.pain)})</td>
        <td>${FLOW_LABELS[log.flow]}</td>
        <td>${log.heartRate ?? '—'}</td>
        <td>${log.hrv ?? '—'}</td>
        <td>${escapeHtml(log.symptoms.join(', ') || '—')}</td>
      </tr>`,
    )
    .join('\n');

  const symptomRows = stats.symptomFreq
    .map(
      (s) =>
        `<tr><td>${escapeHtml(s.name)}</td><td>${s.count} day${s.count === 1 ? '' : 's'}</td></tr>`,
    )
    .join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Better Me Progress Report</title>
<style>
  body { font-family: -apple-system, 'Segoe UI', Arial, sans-serif; color: #33283b; margin: 40px auto; max-width: 760px; line-height: 1.5; }
  h1 { color: #b0246b; font-size: 26px; margin-bottom: 4px; }
  h2 { color: #6d51b8; font-size: 19px; margin-top: 32px; border-bottom: 2px solid #eadcf7; padding-bottom: 6px; }
  .meta { color: #7768a3; font-size: 14px; margin-bottom: 24px; }
  table { border-collapse: collapse; width: 100%; font-size: 14px; margin-top: 12px; }
  th, td { border: 1px solid #e5dcef; padding: 8px 10px; text-align: left; }
  th { background: #f6eefb; }
  .cards { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 12px; }
  .card { border: 1px solid #e5dcef; border-radius: 12px; padding: 14px 18px; min-width: 150px; background: #fdf9fd; }
  .card .value { font-size: 24px; font-weight: 700; color: #33283b; }
  .card .label { font-size: 13px; color: #7768a3; }
  .note { background: #fdeef5; border: 1px solid #f5c6dd; border-radius: 12px; padding: 14px 18px; margin-top: 16px; font-size: 14px; }
  @media print { body { margin: 0; } }
</style>
</head>
<body>
<h1>Better Me — Progress Report</h1>
<p class="meta">Generated ${generatedAt.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} · Covers the last 30 days of logged symptoms</p>

<h2>Summary (last 30 days)</h2>
<div class="cards">
  <div class="card"><div class="value">${stats.avgPain.toFixed(1)}/10</div><div class="label">Average pain</div></div>
  <div class="card"><div class="value">${stats.maxPain}/10</div><div class="label">Worst pain day</div></div>
  <div class="card"><div class="value">${stats.flareDays}</div><div class="label">High-pain days (≥7)</div></div>
  <div class="card"><div class="value">${stats.difficultDays}</div><div class="label">Days with pain ≥4</div></div>
  <div class="card"><div class="value">${stats.avgHeartRate ?? '—'}</div><div class="label">Avg resting HR (bpm)</div></div>
  <div class="card"><div class="value">${stats.avgHrv ?? '—'}</div><div class="label">Avg HRV (ms)</div></div>
  <div class="card"><div class="value">${prediction ? prediction.cycleLength : '—'}</div><div class="label">Cycle length (days)</div></div>
</div>

${
  prediction
    ? `<div class="note"><strong>Prediction:</strong> next period expected around <strong>${formatShort(prediction.nextPeriodStart)}</strong>. High-pain flare-up likely ${formatShort(prediction.flareDates[0])} – ${formatShort(prediction.flareDates[prediction.flareDates.length - 1])}. Last recorded period start: ${formatShort(prediction.lastPeriodStart)}.</div>`
    : ''
}

<h2>Most frequent symptoms</h2>
<table>
  <thead><tr><th>Symptom</th><th>Days reported</th></tr></thead>
  <tbody>
${symptomRows}
  </tbody>
</table>

<h2>Daily log (most recent first)</h2>
<table>
  <thead><tr><th>Date</th><th>Pain</th><th>Flow</th><th>Resting HR</th><th>HRV (ms)</th><th>Symptoms</th></tr></thead>
  <tbody>
${rows}
  </tbody>
</table>

<p class="meta" style="margin-top:32px">Prepared with Better Me. This report summarizes self-reported symptoms and wearable-synced metrics (heart rate / HRV) to support a clinical conversation; it is not a diagnosis.</p>
</body>
</html>`;
}

/** Trigger a browser download of the doctor report. */
export function downloadDoctorReport(
  logs: DayLog[],
  stats: CycleStats,
  prediction: CyclePrediction | null,
): void {
  const html = buildDoctorReportHtml(logs, stats, prediction);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `better-me-doctor-summary-${toISODate(new Date())}.html`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
